import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";
import LibraryBulkImport from "./LibraryBulkImport";

const tabs = [
  { key: "items", label: "Library Items", icon: "bi-book" },
  { key: "borrow", label: "Borrow / Return", icon: "bi-arrow-left-right" },
  { key: "history", label: "Borrowing History", icon: "bi-clock-history" },
  { key: "categories", label: "Categories", icon: "bi-tags" },
  { key: "import", label: "Bulk Import", icon: "bi-file-earmark-spreadsheet" },
];

const emptyCategory = {
  id: null,
  name: "",
  code: "",
  description: "",
  is_active: true,
};

const emptyItem = {
  id: null,
  category: "",
  title: "",
  item_type: "BOOK",
  isbn: "",
  author: "",
  publisher: "",
  edition: "",
  publication_year: "",
  shelf_location: "",
  accession_number: "",
  total_copies: 1,
  available_copies: 1,
  external_url: "",
  status: "ACTIVE",
  notes: "",
};

const emptyBorrow = {
  item: "",
  borrower: "",
  borrowed_date: "",
  due_date: "",
  condition_out: "",
  notes: "",
};

function Library() {
  const [activeTab, setActiveTab] = useState("items");
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({});
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [borrowings, setBorrowings] = useState([]);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [categoryForm, setCategoryForm] = useState(emptyCategory);
  const [itemForm, setItemForm] = useState(emptyItem);
  const [borrowForm, setBorrowForm] = useState(emptyBorrow);
  const [coverFile, setCoverFile] = useState(null);
  const [digitalFile, setDigitalFile] = useState(null);
  const [search, setSearch] = useState("");

  const borrowers = useMemo(() => {
    const studentOptions = students.map((student) => ({
      id: student.id,
      label: `${student.student_number} - ${student.full_name}`,
    }));
    const teacherOptions = teachers.map((teacher) => ({
      id: teacher.id,
      label: `${teacher.staff_number} - ${teacher.full_name}`,
    }));
    return [...studentOptions, ...teacherOptions];
  }, [students, teachers]);

  const visibleItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return items;
    return items.filter((item) =>
      [item.title, item.author, item.isbn, item.accession_number, item.category_name]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [items, search]);

  const activeBorrowings = borrowings.filter((item) => ["BORROWED", "OVERDUE"].includes(item.status));

  const loadAll = async () => {
    try {
      setLoading(true);
      await axiosInstance.post("library/bootstrap/");
      const [
        summaryResponse,
        categoriesResponse,
        itemsResponse,
        borrowingsResponse,
        studentsResponse,
        teachersResponse,
      ] = await Promise.all([
        axiosInstance.get("library/summary/"),
        axiosInstance.get("library/categories/"),
        axiosInstance.get("library/items/"),
        axiosInstance.get("library/borrowings/"),
        axiosInstance.get("students/"),
        axiosInstance.get("teachers/"),
      ]);

      setSummary(summaryResponse.data || {});
      setCategories(categoriesResponse.data || []);
      setItems(itemsResponse.data || []);
      setBorrowings(borrowingsResponse.data || []);
      setStudents(studentsResponse.data || []);
      setTeachers(teachersResponse.data || []);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load library.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const saveCategory = async (event) => {
    event.preventDefault();
    try {
      setLoading(true);
      if (categoryForm.id) {
        await axiosInstance.patch(`library/categories/${categoryForm.id}/`, categoryForm);
        toast.success("Category updated.");
      } else {
        await axiosInstance.post("library/categories/", categoryForm);
        toast.success("Category added.");
      }
      setCategoryForm(emptyCategory);
      await loadAll();
    } catch (error) {
      showError(error, "Failed to save category.");
    } finally {
      setLoading(false);
    }
  };

  const saveItem = async (event) => {
    event.preventDefault();
    try {
      setLoading(true);
      const payload = new FormData();
      Object.entries(itemForm).forEach(([key, value]) => {
        if (key !== "id" && value !== "" && value !== null && value !== undefined) {
          payload.append(key, value);
        }
      });
      if (coverFile) payload.append("cover_image", coverFile);
      if (digitalFile) payload.append("digital_file", digitalFile);

      const config = { headers: { "Content-Type": "multipart/form-data" } };
      if (itemForm.id) {
        await axiosInstance.patch(`library/items/${itemForm.id}/`, payload, config);
        toast.success("Library item updated.");
      } else {
        await axiosInstance.post("library/items/", payload, config);
        toast.success("Library item added.");
      }
      setItemForm(emptyItem);
      setCoverFile(null);
      setDigitalFile(null);
      await loadAll();
    } catch (error) {
      showError(error, "Failed to save library item.");
    } finally {
      setLoading(false);
    }
  };

  const editItem = (item) => {
    setItemForm({
      id: item.id,
      category: item.category || "",
      title: item.title || "",
      item_type: item.item_type || "BOOK",
      isbn: item.isbn || "",
      author: item.author || "",
      publisher: item.publisher || "",
      edition: item.edition || "",
      publication_year: item.publication_year || "",
      shelf_location: item.shelf_location || "",
      accession_number: item.accession_number || "",
      total_copies: item.total_copies || 1,
      available_copies: item.available_copies || 0,
      external_url: item.external_url || "",
      status: item.status || "ACTIVE",
      notes: item.notes || "",
    });
    setCoverFile(null);
    setDigitalFile(null);
    setActiveTab("items");
  };

  const borrowItem = async (event) => {
    event.preventDefault();
    try {
      setLoading(true);
      await axiosInstance.post("library/borrow/", cleanPayload(borrowForm));
      toast.success("Item borrowed successfully.");
      setBorrowForm(emptyBorrow);
      await loadAll();
    } catch (error) {
      showError(error, "Failed to borrow item.");
    } finally {
      setLoading(false);
    }
  };

  const returnItem = async (borrowing, returnStatus = "RETURNED") => {
    const label = returnStatus === "RETURNED" ? "Return this item?" : `Mark this item as ${returnStatus.toLowerCase()}?`;
    if (!window.confirm(label)) return;
    try {
      setLoading(true);
      await axiosInstance.post(`library/borrowings/${borrowing.id}/return/`, {
        status: returnStatus,
      });
      toast.success("Borrowing updated.");
      await loadAll();
    } catch (error) {
      showError(error, "Failed to update borrowing.");
    } finally {
      setLoading(false);
    }
  };

  const deleteRecord = async (url, label) => {
    if (!window.confirm(`Delete ${label}?`)) return;
    try {
      setLoading(true);
      await axiosInstance.delete(url);
      toast.success(`${label} deleted.`);
      await loadAll();
    } catch (error) {
      toast.error(`Could not delete ${label}. It may already be in use.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="card border-0 shadow-sm mb-3">
        <div className="card-body">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
            <div>
              <h5 className="fw-bold mb-1">Library</h5>
              <p className="text-muted mb-0">
                Manage books, digital resources, borrowing, returns, overdue items, and library history.
              </p>
            </div>
            <button className="btn btn-outline-success" onClick={loadAll} disabled={loading}>
              <i className="bi bi-arrow-clockwise me-2"></i>
              Refresh
            </button>
          </div>
          <div className="row g-2 mt-3">
            <Summary label="Items" value={summary.items || 0} />
            <Summary label="Available Copies" value={summary.available_copies || 0} />
            <Summary label="Borrowed" value={summary.borrowed || 0} />
            <Summary label="Overdue" value={summary.overdue || 0} />
          </div>
        </div>
      </div>

      <div className="d-flex flex-wrap gap-2 mb-3">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`btn btn-sm ${activeTab === tab.key ? "btn-success" : "btn-light border"}`}
            onClick={() => setActiveTab(tab.key)}
          >
            <i className={`bi ${tab.icon} me-2`}></i>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "items" && (
        <SetupCard title="Library Items">
          <form className="row g-3 mb-4" onSubmit={saveItem}>
            <TextField label="Title" value={itemForm.title} onChange={(value) => setItemForm((prev) => ({ ...prev, title: value }))} required />
            <SelectField label="Category" value={itemForm.category} onChange={(value) => setItemForm((prev) => ({ ...prev, category: value }))} options={categories.filter((item) => item.is_active).map((item) => [item.id, item.name])} required={false} />
            <SelectField label="Item Type" value={itemForm.item_type} onChange={(value) => setItemForm((prev) => ({ ...prev, item_type: value }))} options={[
              ["BOOK", "Book"],
              ["TEXTBOOK", "Textbook"],
              ["REFERENCE", "Reference"],
              ["MAGAZINE", "Magazine"],
              ["DIGITAL", "Digital Resource"],
              ["OTHER", "Other"],
            ]} />
            <TextField label="ISBN" value={itemForm.isbn} onChange={(value) => setItemForm((prev) => ({ ...prev, isbn: value }))} />
            <TextField label="Author" value={itemForm.author} onChange={(value) => setItemForm((prev) => ({ ...prev, author: value }))} />
            <TextField label="Publisher" value={itemForm.publisher} onChange={(value) => setItemForm((prev) => ({ ...prev, publisher: value }))} />
            <TextField label="Edition" value={itemForm.edition} onChange={(value) => setItemForm((prev) => ({ ...prev, edition: value }))} />
            <TextField type="number" label="Publication Year" value={itemForm.publication_year} onChange={(value) => setItemForm((prev) => ({ ...prev, publication_year: value }))} />
            <TextField label="Shelf Location" value={itemForm.shelf_location} onChange={(value) => setItemForm((prev) => ({ ...prev, shelf_location: value }))} />
            <TextField label="Accession Number" value={itemForm.accession_number} onChange={(value) => setItemForm((prev) => ({ ...prev, accession_number: value }))} />
            <TextField type="number" label="Total Copies" value={itemForm.total_copies} onChange={(value) => setItemForm((prev) => ({ ...prev, total_copies: value }))} required />
            <TextField type="number" label="Available Copies" value={itemForm.available_copies} onChange={(value) => setItemForm((prev) => ({ ...prev, available_copies: value }))} required />
            <SelectField label="Status" value={itemForm.status} onChange={(value) => setItemForm((prev) => ({ ...prev, status: value }))} options={[
              ["ACTIVE", "Active"],
              ["ARCHIVED", "Archived"],
              ["LOST", "Lost"],
              ["DAMAGED", "Damaged"],
            ]} />
            <TextField label="External URL" value={itemForm.external_url} onChange={(value) => setItemForm((prev) => ({ ...prev, external_url: value }))} />
            <div className="col-md-4">
              <label className="form-label fw-semibold">Cover Image</label>
              <input type="file" className="form-control" accept="image/*" onChange={(event) => setCoverFile(event.target.files?.[0] || null)} />
            </div>
            <div className="col-md-4">
              <label className="form-label fw-semibold">Digital File</label>
              <input type="file" className="form-control" onChange={(event) => setDigitalFile(event.target.files?.[0] || null)} />
            </div>
            <div className="col-12">
              <input className="form-control" placeholder="Notes" value={itemForm.notes || ""} onChange={(event) => setItemForm((prev) => ({ ...prev, notes: event.target.value }))} />
            </div>
            <SaveBar loading={loading} label={itemForm.id ? "Update Item" : "Add Item"} onCancel={itemForm.id ? () => setItemForm(emptyItem) : null} />
          </form>

          <div className="d-flex justify-content-between align-items-center gap-2 mb-3">
            <h6 className="fw-bold mb-0">Catalogue</h6>
            <input className="form-control" style={{ maxWidth: 320 }} placeholder="Search title, author, ISBN..." value={search} onChange={(event) => setSearch(event.target.value)} />
          </div>

          <SimpleTable
            headers={["Title", "Category", "Author", "Type", "Copies", "Shelf", "Status", "Digital", "Actions"]}
            rows={visibleItems.map((item) => [
              <div><div className="fw-semibold">{item.title}</div><small className="text-muted">{item.isbn || item.accession_number || ""}</small></div>,
              item.category_name || "-",
              item.author || "-",
              item.item_type,
              `${item.available_copies}/${item.total_copies}`,
              item.shelf_location || "-",
              <Status value={item.status} />,
              item.digital_file_url ? <a href={item.digital_file_url} target="_blank" rel="noreferrer">Open</a> : item.external_url ? <a href={item.external_url} target="_blank" rel="noreferrer">Link</a> : "-",
              <ActionButtons onEdit={() => editItem(item)} onDelete={() => deleteRecord(`library/items/${item.id}/`, item.title)} />,
            ])}
          />
        </SetupCard>
      )}

      {activeTab === "borrow" && (
        <SetupCard title="Borrow and Return">
          <form className="row g-3 mb-4" onSubmit={borrowItem}>
            <SelectField label="Library Item" value={borrowForm.item} onChange={(value) => setBorrowForm((prev) => ({ ...prev, item: value }))} options={items.filter((item) => item.status === "ACTIVE" && Number(item.available_copies) > 0).map((item) => [item.id, `${item.title} (${item.available_copies} available)`])} />
            <SelectField label="Borrower" value={borrowForm.borrower} onChange={(value) => setBorrowForm((prev) => ({ ...prev, borrower: value }))} options={borrowers.map((item) => [item.id, item.label])} />
            <TextField type="date" label="Borrowed Date" value={borrowForm.borrowed_date} onChange={(value) => setBorrowForm((prev) => ({ ...prev, borrowed_date: value }))} />
            <TextField type="date" label="Due Date" value={borrowForm.due_date} onChange={(value) => setBorrowForm((prev) => ({ ...prev, due_date: value }))} required />
            <TextField label="Condition Out" value={borrowForm.condition_out} onChange={(value) => setBorrowForm((prev) => ({ ...prev, condition_out: value }))} />
            <TextField label="Notes" value={borrowForm.notes} onChange={(value) => setBorrowForm((prev) => ({ ...prev, notes: value }))} />
            <SaveBar loading={loading} label="Borrow Item" />
          </form>

          <SimpleTable
            headers={["Borrower", "Item", "Borrowed", "Due", "Status", "Actions"]}
            rows={activeBorrowings.map((borrowing) => [
              <div><div className="fw-semibold">{borrowing.borrower_name}</div><small className="text-muted">{borrowing.borrower_number}</small></div>,
              borrowing.item_title,
              borrowing.borrowed_date,
              borrowing.due_date,
              <Status value={borrowing.is_overdue ? "OVERDUE" : borrowing.status} />,
              <div className="d-flex flex-wrap gap-1">
                <button className="btn btn-sm btn-success" onClick={() => returnItem(borrowing, "RETURNED")}>Return</button>
                <button className="btn btn-sm btn-light border" onClick={() => returnItem(borrowing, "DAMAGED")}>Damaged</button>
                <button className="btn btn-sm btn-light border" onClick={() => returnItem(borrowing, "LOST")}>Lost</button>
              </div>,
            ])}
          />
        </SetupCard>
      )}

      {activeTab === "history" && (
        <SetupCard title="Borrowing History">
          <SimpleTable
            headers={["Borrower", "Type", "Item", "Borrowed", "Due", "Returned", "Fine", "Status"]}
            rows={borrowings.map((borrowing) => [
              <div><div className="fw-semibold">{borrowing.borrower_name}</div><small className="text-muted">{borrowing.borrower_number}</small></div>,
              borrowing.borrower_type,
              borrowing.item_title,
              borrowing.borrowed_date,
              borrowing.due_date,
              borrowing.returned_date || "-",
              borrowing.fine_amount || 0,
              <Status value={borrowing.is_overdue ? "OVERDUE" : borrowing.status} />,
            ])}
          />
        </SetupCard>
      )}

      {activeTab === "import" && (
        <LibraryBulkImport onImported={loadAll} />
      )}

      {activeTab === "categories" && (
        <SetupCard title="Library Categories">
          <form className="row g-3 mb-4" onSubmit={saveCategory}>
            <TextField label="Name" value={categoryForm.name} onChange={(value) => setCategoryForm((prev) => ({ ...prev, name: value }))} required />
            <TextField label="Code" value={categoryForm.code} onChange={(value) => setCategoryForm((prev) => ({ ...prev, code: value.toUpperCase() }))} required />
            <SwitchField label="Active" checked={categoryForm.is_active} onChange={(value) => setCategoryForm((prev) => ({ ...prev, is_active: value }))} />
            <div className="col-12">
              <input className="form-control" placeholder="Description" value={categoryForm.description || ""} onChange={(event) => setCategoryForm((prev) => ({ ...prev, description: event.target.value }))} />
            </div>
            <SaveBar loading={loading} label={categoryForm.id ? "Update Category" : "Add Category"} onCancel={categoryForm.id ? () => setCategoryForm(emptyCategory) : null} />
          </form>

          <SimpleTable
            headers={["Name", "Code", "Status", "Actions"]}
            rows={categories.map((category) => [
              category.name,
              category.code,
              <Status value={category.is_active ? "ACTIVE" : "INACTIVE"} />,
              <ActionButtons onEdit={() => setCategoryForm(category)} onDelete={() => deleteRecord(`library/categories/${category.id}/`, category.name)} />,
            ])}
          />
        </SetupCard>
      )}
    </div>
  );
}

function cleanPayload(values) {
  const payload = {};
  Object.entries(values).forEach(([key, value]) => {
    if (value !== "" && value !== null && value !== undefined) payload[key] = value;
  });
  return payload;
}

function showError(error, fallback) {
  const data = error.response?.data;
  const message = data?.detail || Object.values(data || {})?.flat?.()?.[0] || fallback;
  toast.error(message);
}

function Summary({ label, value }) {
  return (
    <div className="col-6 col-xl-3">
      <div className="border rounded-3 p-3 bg-light h-100">
        <small className="text-muted fw-bold">{label}</small>
        <div className="fs-5 fw-bold" style={{ color: "#125b2d" }}>{value}</div>
      </div>
    </div>
  );
}

function SetupCard({ title, children }) {
  return (
    <div className="card border-0 shadow-sm">
      <div className="card-header bg-white"><h6 className="fw-bold mb-0">{title}</h6></div>
      <div className="card-body">{children}</div>
    </div>
  );
}

function TextField({ label, value, onChange, placeholder = "", type = "text", required = false }) {
  return (
    <div className="col-md-4">
      <label className="form-label fw-semibold">{label}</label>
      <input type={type} className="form-control" placeholder={placeholder} value={value || ""} onChange={(event) => onChange(event.target.value)} required={required} />
    </div>
  );
}

function SelectField({ label, value, onChange, options, required = true }) {
  return (
    <div className="col-md-4">
      <label className="form-label fw-semibold">{label}</label>
      <select className="form-select" value={value || ""} onChange={(event) => onChange(event.target.value)} required={required}>
        <option value="">Select {label.toLowerCase()}</option>
        {options.map(([optionValue, optionLabel]) => <option key={optionValue} value={optionValue}>{optionLabel}</option>)}
      </select>
    </div>
  );
}

function SwitchField({ label, checked, onChange }) {
  return (
    <div className="col-md-4 d-flex align-items-end">
      <label className="form-check form-switch border rounded-3 p-3 w-100 mb-0">
        <input type="checkbox" className="form-check-input ms-0 me-2" checked={Boolean(checked)} onChange={(event) => onChange(event.target.checked)} />
        <span className="form-check-label fw-semibold">{label}</span>
      </label>
    </div>
  );
}

function SaveBar({ loading, label, onCancel }) {
  return (
    <div className="col-12 d-flex flex-wrap gap-2 mt-3">
      <button className="btn btn-success fw-semibold" disabled={loading}>{loading ? "Saving..." : label}</button>
      {onCancel && <button type="button" className="btn btn-light border fw-semibold" onClick={onCancel}>Cancel Edit</button>}
    </div>
  );
}

function Status({ value }) {
  const normalized = String(value || "").toUpperCase();
  const className = ["ACTIVE", "BORROWED", "RETURNED"].includes(normalized)
    ? "bg-success"
    : ["OVERDUE", "DAMAGED"].includes(normalized)
      ? "bg-warning text-dark"
      : normalized === "LOST"
        ? "bg-danger"
        : "bg-secondary";
  return <span className={`badge ${className}`}>{String(value || "-").replaceAll("_", " ")}</span>;
}

function ActionButtons({ onEdit, onDelete }) {
  return (
    <div className="d-flex align-items-center gap-1">
      <button type="button" className="btn btn-sm btn-light border" title="Edit" onClick={onEdit}><i className="bi bi-pencil-square text-success"></i></button>
      <button type="button" className="btn btn-sm btn-light border" title="Delete" onClick={onDelete}><i className="bi bi-trash text-danger"></i></button>
    </div>
  );
}

function SimpleTable({ headers, rows }) {
  return (
    <div className="table-responsive">
      <table className="table table-hover align-middle mb-0">
        <thead className="table-light"><tr>{headers.map((header) => <th key={header}>{header}</th>)}</tr></thead>
        <tbody>
          {rows.length ? rows.map((row, index) => (
            <tr key={index}>{row.map((cell, cellIndex) => <td key={cellIndex}>{cell}</td>)}</tr>
          )) : (
            <tr><td colSpan={headers.length} className="text-center text-muted py-4">No records found.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default Library;
